import type { FC } from 'react';
import { memo, useCallback, useEffect, useRef } from 'react';

import { LoaderBlock } from '../../components/LoaderBlock';
import type { GraphEditorInstance } from '../../editor/editor-model';
import { useAppApolloClient } from '../../hooks/useAppApolloClient';
import { usePermissions } from '../../hooks/usePermissions';
import { useRefLatest } from '../../hooks/useRefLatest';
import { useAppDispatch } from '../../redux/hooks';
import { openYakliNewResponseChildInBbf } from '../../redux/slices/yakli-slice';
import type { VoteRef } from '../../services/scrollSections';
import { useScrollSectionsHelpers } from '../../services/scrollSections';
import type { Message } from '../../types/api';
import { useLoginWithPromise } from '../../utils/graphlogin/AuthWithPromise-context';
import { isMobile } from '../../utils/web';
import type { CreateOrUpdateMessageVariablesSmall } from '../api/message';
import { useThreadContext } from '../ThreadContexts/ThreadContext';
import { useIsEditingPermissionAvailable } from '../ThreadContexts/ThreadEditingContext';
import { useThreadInitContext } from '../ThreadContexts/ThreadInitContext';
import { type EditorCloseCallback, ThreadEditorForm } from '../Write/ThreadEditorForm';
import { newResponseKeyInBbf, submitAnswer } from './thread-service';
import { type MessageId, openNewResponseChildInBbf } from './thread-slice';
import { ThreadMessageAnswerBlock } from './ThreadMessageAnswerBlock';

/**
 * Important note: on mobile, the editor is not rendered here. See the comments in packages/front/src/thread/ThreadFooterEditor.tsx for explanations.
 */

interface ThreadMessageOrEditorProps {
  currentMessageId: MessageId | undefined;
  parentMessageId: string | undefined;
  onSubmitNewAnswer: EditorCloseCallback | undefined;
  onCloseEditor: (result: Message | undefined) => void;
  targetedId: string | undefined;
  unlimited: boolean | undefined;
  voteRef: VoteRef;
  isYakliDisplay?: boolean;
}

export const ThreadMessageOrEditor: FC<ThreadMessageOrEditorProps> = memo(
  function ThreadMessageOrEditor(props) {
    const {
      currentMessageId,
      parentMessageId,
      onSubmitNewAnswer,
      onCloseEditor,
      targetedId,
      unlimited,
      voteRef,
      isYakliDisplay,
    } = props;

    const currentMessageIdRef = useRefLatest(currentMessageId);
    const editorRef = useRef<GraphEditorInstance>(null);

    const client = useAppApolloClient();
    const dispatch = useAppDispatch();

    const currentMessageIsNewResponse = currentMessageId === newResponseKeyInBbf;
    const answerEditorId = `new-answer-to-message-${currentMessageId}`;

    const onSubmitNewAnswerRef = useRefLatest(onSubmitNewAnswer);

    const { slug } = useThreadContext();
    const slugRef = useRefLatest(slug);

    const parentMessageIdRef = useRefLatest(parentMessageId);

    const handleSubmitNewAnswer = useCallback(
      async (values: CreateOrUpdateMessageVariablesSmall) => {
        const result = await submitAnswer(
          slugRef.current,
          parentMessageIdRef.current,
          client,
          values,
          true,
          isYakliDisplay,
        );

        // Triggers the vote on the parent
        onSubmitNewAnswerRef.current?.(result);
      },
      [client, isYakliDisplay, onSubmitNewAnswerRef, parentMessageIdRef, slugRef],
    );

    const closeEditorNoArg = useCallback(() => {
      onCloseEditor(undefined);
    }, [onCloseEditor]);

    const { forceCurrentScrollSection } = useScrollSectionsHelpers();

    const { authUserId } = usePermissions();
    const loginWithPromise = useLoginWithPromise();

    const startAnswer = useCallback(async () => {
      if (isMobile()) {
        // On mobile, the editor is not rendered here, but in ThreadFooterEditor instead.
        if (!authUserId) {
          await loginWithPromise(); // workaround as we do not display MobileThreadFooterEditor if not authenticated
        } else {
          if (currentMessageId) {
            forceCurrentScrollSection(currentMessageId);
          } else {
            console.warn('messageId undefined, cannot answer to the thread or message.');
          }
        }
      } else {
        // Opens an editor to write a new child comment as answer to the current comment. It creates a temporary child to render the editor.
        const responseSelector = isYakliDisplay
          ? openYakliNewResponseChildInBbf
          : openNewResponseChildInBbf;
        dispatch(responseSelector(currentMessageIdRef.current));
      }
    }, [
      authUserId,
      loginWithPromise,
      currentMessageId,
      forceCurrentScrollSection,
      isYakliDisplay,
      dispatch,
      currentMessageIdRef,
    ]);

    const isTargeted = targetedId === currentMessageId;
    const { isAnswering } = useThreadContext();
    const isInit = useThreadInitContext();

    // If the thread is opened in answer mode, the editor for new answer should be opened below the target node.
    useEffect(() => {
      if (isTargeted && isAnswering && isInit) {
        startAnswer();
      }
    }, [isAnswering, isInit, isTargeted, startAnswer]);

    useEffect(() => {
      if (editorRef.current) {
        setTimeout(() => {
          // Wait for the editor to be rendered
          editorRef.current?.focus();
        }, 250);
      }
    }, []);

    const isEditingPermissionAvailable = useIsEditingPermissionAvailable();

    if (!isEditingPermissionAvailable) return <LoaderBlock />;

    if (!currentMessageId) return null;

    return (
      <>
        {currentMessageIsNewResponse && (
          // When writing a new message in response to another message.
          <ThreadEditorForm
            editorRef={editorRef}
            baseId={answerEditorId}
            onSubmitAnswer={handleSubmitNewAnswer}
            onCancelAnswer={closeEditorNoArg}
            noBorder
            withAvatar
          />
        )}

        {!currentMessageIsNewResponse && currentMessageId && (
          <>
            {/* <ShowIdStatusForDebug messageId={currentMessageId} /> */}
            <ThreadMessageAnswerBlock
              key={currentMessageId}
              messageId={currentMessageId}
              targetedId={targetedId}
              unlimited={unlimited}
              isNestedAnswer={true}
              voteRef={voteRef}
              onAnswer={startAnswer}
              isYakliDisplay={isYakliDisplay}
            />
          </>
        )}
      </>
    );
  },
);

// export const ShowIdStatusForDebug: FC<{ messageId: MessageId | undefined }> = ({ messageId }) => {
//   const status = useAppSelector(s => getStateMsg(s, messageId)?.status);
//   const position = useAppSelector(s => getStateMsg(s, messageId)?.position);
//   return frontConfig.IS_DEV ? (
//     <div>
//       {messageId} - {status || 'No status'} - position {position}
//     </div>
//   ) : null;
// };
